import PropTypes from "prop-types";
import { connect } from "react-redux";
import compose from "recompose/compose";
import moment from "moment";
import { initialize, change } from "redux-form";

import { translate, toggleFilter, crudGetList, changeFilterParams } from "jazasoft";
import { useDeepCallback, useDeepEffect, useDeepMemo, useQuery } from "../../../hooks";
import { isEmpty } from "../../../utils/helpers";
import { UOM } from "../../../constants";

const getSearchText = filterValues => {
  const { start, end, productIds, ...values } = filterValues || {};
  let searchItems = [];
  if (start && end) {
    searchItems.push(`orderCloseDate=ge=${start}`);
    searchItems.push(`orderCloseDate=le=${end}`);
  }
  const items = Object.keys(values)
    .filter(key => !isEmpty(values[key]))
    .map(key => (Array.isArray(values[key]) ? `${key}=in=(${values[key].map(e => `"${e}"`).join(",")})` : `${key}=='${values[key]}'`));

  return [...searchItems, ...items, `referencePart=='TRUE'`].join(";");
};

const filterDefaultValues = defaultMonths => ({
  orderCloseDate: {
    start: defaultMonths === 0 ? moment().startOf("day").valueOf() : moment().subtract(defaultMonths, "months").startOf("day").valueOf(),
    end: moment().endOf("day").valueOf()
  }
});

const MacroController = props => {
  const {
    dataEndpoint,
    defaultMonths,
    i18nKey,
    data,
    products,
    fabricPatterns,
    fabricCategories,
    seasons,
    customers,
    suppliers,
    filterValues,
    filter,
    isLoading,
    children,
    version,
    translate,
    filterOpen,
    toggleFilter
  } = props;

  const finalFilter = useDeepMemo(() => {
    if (isEmpty(filterValues)) return {};
    const { start, end, fabricUom, unitId, unitIds, line, customerIds, seasonId, productIds, supplierIds, fabricPatternIds, fabricCategoryIds } =
      filterValues || {};
    return {
      fabricUom: fabricUom === UOM.METER.value ? undefined : fabricUom,
      unitId,
      unitIds,
      line,
      customer: !isEmpty(customerIds) && !isEmpty(customers) ? customerIds.map(id => customers[id]?.name).filter(e => !isEmpty(e)) : [],
      season: seasonId && seasons[seasonId] && seasons[seasonId].name,
      product: !isEmpty(productIds) && !isEmpty(products) ? productIds.map(id => products[id]?.name).filter(e => !isEmpty(e)) : [],
      supplier: !isEmpty(supplierIds) && !isEmpty(suppliers) ? supplierIds.map(id => suppliers[id]?.name).filter(e => !isEmpty(e)) : [],
      fabricPattern:
        !isEmpty(fabricPatternIds) && !isEmpty(fabricPatterns) ? fabricPatternIds.map(id => fabricPatterns[id]?.name).filter(e => !isEmpty(e)) : [],
      fabricCategory:
        !isEmpty(fabricCategoryIds) && !isEmpty(fabricCategories)
          ? fabricCategoryIds.map(id => fabricCategories[id]?.name).filter(e => !isEmpty(e))
          : [],
      start: moment(start).startOf("day").valueOf(),
      end: moment(end).endOf("day").valueOf()
    };
  }, [filterValues, customers, seasons, products, suppliers, fabricPatterns, fabricCategories]);

  const searchText = getSearchText(finalFilter);

  const { data: settings = [] } = useQuery({ url: `settings`, params: { size: 1000 } });
  const { data: reportList = [] } = useQuery(!isEmpty(searchText) && !isEmpty(dataEndpoint) && { url: dataEndpoint, params: { search: searchText } });

  //TODO: Store State in Local State
  const setFilter = useDeepCallback(
    values => {
      const { initialize, changeFilterParams, change } = props;
      if (Object.keys(values).length === 0) {
        initialize("filter-form", filterDefaultValues(defaultMonths)["orderCloseDate"]);
      } else {
        if (values["season"]) {
          const { orderCloseDate: dateValue, ...restValues } = values;
          changeFilterParams("reports", restValues);

          change("filter-form", "start", null);
          change("filter-form", "end", null);
        } else {
          values = { ...filterDefaultValues(defaultMonths), ...values };
          changeFilterParams("reports", values);
          const defaultValues = filterDefaultValues(defaultMonths)["orderCloseDate"] || {};
          if (filterValues && !filterValues.start) {
            change("filter-form", "start", defaultValues.start);
          }
          if (filterValues && !filterValues.end) {
            change("filter-form", "end", defaultValues.end);
          }
        }
      }
    },
    [filterValues, defaultMonths]
  );

  useDeepEffect(() => {
    setFilter(defaultMonths);
  }, [defaultMonths]);

  if (!children) return null;
  const orderList = reportList || [];
  return children({
    i18nKey,
    resource: "reports",
    data,
    products,
    fabricPatterns,
    fabricCategories,
    suppliers,
    seasons,
    customers,
    orderList,
    isLoading,
    toggleFilter,
    setFilter: setFilter,
    filter,
    filterValues,
    filterDefaultValues: filterDefaultValues(defaultMonths),
    filterOpen,
    translate,
    version,
    settings
  });
};

MacroController.propTypes = {
  //props provided by user
  dataEndpoint: PropTypes.oneOf(["reports", "report-detail", "report-summary"]),
  defaultMonths: PropTypes.number,
  className: PropTypes.string,
  classes: PropTypes.object,
  children: PropTypes.func.isRequired,
  requestConfig: PropTypes.object,
  format: PropTypes.func,
  filterOnly: PropTypes.bool, // Use for filter only not for fetching data
  //props from state
  record: PropTypes.object,
  isLoading: PropTypes.bool,
  translate: PropTypes.func.isRequired,
  // from framework
  basePath: PropTypes.string,
  resource: PropTypes.string,
  referenceResource: PropTypes.string,
  i18nKey: PropTypes.string
};

MacroController.defaultProps = {
  dataEndpoint: "report-detail",
  defaultMonths: 3,
  filterOnly: false
};

const mapStateToProps = (state, props) => {
  const resourceState = state.jazasoft.resources["reports"];
  return {
    data: resourceState && resourceState.data,
    products: state.jazasoft.resources["products"] && state.jazasoft.resources["products"].data,
    fabricPatterns: state.jazasoft.resources["fabric-patterns"] && state.jazasoft.resources["fabric-patterns"].data,
    fabricCategories: state.jazasoft.resources["fabric-categories"] && state.jazasoft.resources["fabric-categories"].data,
    seasons: state.jazasoft.resources["seasons"] && state.jazasoft.resources["seasons"].data,
    customers: state.jazasoft.resources["customers"] && state.jazasoft.resources["customers"].data,
    suppliers: state.jazasoft.resources["suppliers"] && state.jazasoft.resources["suppliers"].data,
    filter: resourceState && resourceState.list && resourceState.list.params && resourceState.list.params.filter,
    filterValues: state.form["filter-form"] && state.form["filter-form"].values,
    filterOpen: state.jazasoft.ui.filterOpen,
    isLoading: state.jazasoft.loading > 0,
    version: state.jazasoft.ui.viewVersion
  };
};

export default compose(
  translate,
  connect(mapStateToProps, {
    crudGetList,
    changeFilterParams,
    toggleFilter,
    initialize,
    change
  })
)(MacroController);
